import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Upload, Download, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import BulkUploadGuide from './BulkUploadGuide';

interface StudentRecord {
  name: string;
  email: string;
  phone?: string;
  department?: string;
  courses?: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  record: StudentRecord;
  rowNumber: number;
}

interface BulkStudentUploadProps {
  onUploadComplete: () => void;
}

const BulkStudentUpload: React.FC<BulkStudentUploadProps> = ({ onUploadComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const downloadTemplate = () => {
    const csvContent = `name,email,phone,department,courses
John Doe,john@example.com,123-456-7890,Engineering,"python-basics,sql-intro"
Jane Smith,jane@example.com,098-765-4321,Marketing,data-science-101
Bob Wilson,bob@example.com,555-123-4567,Sales,"web-development,digital-marketing"`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const parseCSV = (text: string): StudentRecord[] => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const records: StudentRecord[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const record: StudentRecord = {
        name: '',
        email: ''
      };
      
      headers.forEach((header, index) => {
        const value = values[index] || '';
        if (header === 'name') record.name = value;
        else if (header === 'email') record.email = value;
        else if (header === 'phone') record.phone = value;
        else if (header === 'department') record.department = value;
        else if (header === 'courses') record.courses = value;
      });
      
      if (record.name || record.email) {
        records.push(record);
      }
    }
    
    return records;
  };

  const validateRecord = (record: StudentRecord, rowNumber: number): ValidationResult => {
    const errors: string[] = [];
    
    if (!record.name || record.name.length < 2) {
      errors.push('Name is required (min 2 characters)');
    }
    
    if (!record.email) {
      errors.push('Email is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(record.email)) {
        errors.push('Invalid email format');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors,
      record,
      rowNumber
    };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setFile(selectedFile);
    
    // Parse and validate
    const text = await selectedFile.text();
    const records = parseCSV(text);
    
    if (records.length === 0) {
      toast.error('No valid records found in CSV');
      return;
    }
    
    const results = records.map((record, index) => validateRecord(record, index + 2));
    setValidationResults(results);
    
    const validCount = results.filter(r => r.valid).length;
    const invalidCount = results.length - validCount;
    
    if (invalidCount > 0) {
      toast.warning(`${validCount} valid, ${invalidCount} invalid records`);
    } else {
      toast.success(`All ${validCount} records are valid`);
    }
  };

  const handleBulkUpload = async () => {
    const validRecords = validationResults.filter(r => r.valid);
    
    if (validRecords.length === 0) {
      toast.error('No valid records to upload');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const students = validRecords.map(r => ({
        name: r.record.name,
        email: r.record.email,
        phone: r.record.phone,
        department: r.record.department,
        courseIds: r.record.courses ? r.record.courses.split(',').map(c => c.trim()) : []
      }));
      
      const { data, error } = await supabase.functions.invoke('bulk-create-students', {
        body: { students }
      });
      
      if (error) throw error;
      
      if (data?.success) {
        const { successCount, failureCount, results } = data;
        
        if (failureCount === 0) {
          toast.success(`Successfully created ${successCount} students`);
        } else {
          toast.warning(`Created ${successCount} students, ${failureCount} failed`);
        }
        
        setFile(null);
        setValidationResults([]);
        onUploadComplete();
      } else {
        throw new Error(data?.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload students');
    } finally {
      setIsUploading(false);
    }
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.length - validCount;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk Student Upload</CardTitle>
        <CardDescription>
          Upload a CSV file to create multiple student accounts at once
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Guide */}
        <BulkUploadGuide />

        {/* Template Download */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
          <div>
            <h3 className="font-medium">Download Template</h3>
            <p className="text-sm text-muted-foreground">
              Get the CSV template with correct format
            </p>
          </div>
          <Button onClick={downloadTemplate} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>

        {/* File Upload */}
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
              className="flex-1"
            />
            {file && (
              <Badge variant="secondary" className="whitespace-nowrap">
                {file.name}
              </Badge>
            )}
          </div>
        </div>

        {/* Validation Results */}
        {validationResults.length > 0 && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="flex items-center gap-4">
                  <span>
                    <strong>{validCount}</strong> valid records
                  </span>
                  {invalidCount > 0 && (
                    <span className="text-destructive">
                      <strong>{invalidCount}</strong> invalid records
                    </span>
                  )}
                </div>
              </AlertDescription>
            </Alert>

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Row</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Courses</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validationResults.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-sm">
                          {result.rowNumber}
                        </TableCell>
                        <TableCell>
                          {result.valid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-destructive" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div>{result.record.name}</div>
                            {result.errors.length > 0 && (
                              <div className="text-xs text-destructive">
                                {result.errors.join(', ')}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{result.record.email}</TableCell>
                        <TableCell>{result.record.department || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {result.record.courses || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                onClick={() => {
                  setFile(null);
                  setValidationResults([]);
                }}
                variant="outline"
                disabled={isUploading}
              >
                Clear
              </Button>
              <Button
                onClick={handleBulkUpload}
                disabled={validCount === 0 || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating {validCount} Students...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Create {validCount} Students
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkStudentUpload;
